<script lang="ts" setup>
import type { FormInstance } from 'element-plus'
import { ref, toRef } from 'vue'
import type { BookingFormValidationResult } from './bookingTypes'
import type { Container } from '~/models/containers'
import { TERMINALS_REQUIRING_MASTER_BL, getTerminalLabel } from '~/constants'
import LicensePlateSelector from '~/components/inputs/LicensePlateSelector.vue'
import { useContainerBooking } from '~/compositions/useContainerBooking'
import type { GETAutoBookRequest } from '~/services/apiClient'
import type { AppointmentSlot } from '~/models/appointmentSlot'

const props = defineProps<{
  container: Container
  existingSettings?: GETAutoBookRequest
  appointmentSlot?: AppointmentSlot
}>()

const propSettings = toRef(props, 'existingSettings')
const localSettings = ref<GETAutoBookRequest>()
const mergedSettings = computed(() => propSettings.value ?? localSettings.value)

// Internal state
const masterBLFormRef = ref<FormInstance>()
const licensePlateFormRef = ref<FormInstance>()
const {
  statusColor,
  rules,
  form,
  requiresMasterBL,
  usesLicensePlate,
  shouldPrefillLicensePlate,
  licensePlateShouldBeDisabled,
  ownChassis,
  requiresOwnChassisSelector,
} = useContainerBooking({
  container: props.container,
  appointmentSlot: props.appointmentSlot,
  existingSettings: mergedSettings,
})

// Expose validation method for parent component to call
async function validate(): Promise<BookingFormValidationResult> {
  let allFieldsValid = true

  // Validate masterBL form if it exists and is required
  if (
    TERMINALS_REQUIRING_MASTER_BL.size > 0 &&
    requiresMasterBL &&
    masterBLFormRef.value
  ) {
    const isValid = await masterBLFormRef.value.validate()
    if (!isValid) {
      allFieldsValid = false
    }
  }

  // Validate licensePlate form if it exists
  if (licensePlateFormRef.value) {
    const isValid = await licensePlateFormRef.value.validate()
    if (!isValid) {
      allFieldsValid = false
    }
  }
  form.ownChassis = ownChassis.value
  return {
    valid: allFieldsValid,
    formValues: form,
  }
}

defineExpose({
  validate,
})
</script>

<template>
  <tr class="data-row">
    <td>{{ container.number }}</td>
    <td>
      {{
        container.terminal ? getTerminalLabel(container.terminal) : 'Unknown'
      }}
    </td>
    <td>
      {{
        container.last_free_day
          ? container.last_free_day.toFormat('L/d')
          : 'N/A'
      }}
    </td>
    <td>
      <span :style="{ color: statusColor }">
        {{ container.auto_book_request_status }}
      </span>
    </td>

    <td v-if="TERMINALS_REQUIRING_MASTER_BL.size > 0">
      <el-form
        v-if="requiresMasterBL"
        ref="masterBLFormRef"
        :model="form"
        :rules="rules"
        class="cell-form"
      >
        <el-form-item prop="masterBL" class="form-item--compact">
          <el-input v-model="form.masterBL" placeholder="Enter Here">
            <template #prefix>
              <i-la:scroll />
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </td>

    <td>
      <template v-if="requiresOwnChassisSelector">
        <el-switch
          v-model="ownChassis"
          inline-prompt
          active-color="#13ce66"
          inactive-color="#ff4949"
          active-text="Own"
          inactive-text="Pool"
          :disabled="Boolean(container.booked_appointment)"
        />
      </template>
    </td>

    <td>
      <el-form
        ref="licensePlateFormRef"
        :model="form"
        :rules="rules"
        class="cell-form"
      >
        <el-form-item prop="licensePlate" class="form-item--compact">
          <LicensePlateSelector
            v-if="usesLicensePlate && container.terminal"
            v-model="form.licensePlate"
            :auto-select-value="
              shouldPrefillLicensePlate && !container.booked_appointment
            "
            :terminal="container.terminal"
            :disabled="licensePlateShouldBeDisabled"
          />
          <span v-else>
            {{ form.licensePlate }}
          </span>
        </el-form-item>
      </el-form>
    </td>
  </tr>
</template>

<style scoped>
.data-row {
  height: 42px;
}

.cell-form {
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
}

.el-form-item {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.el-input__inner {
  line-height: 0 !important;
}
</style>
